import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";
import { ValueKind } from "../../../model";

/**
 * Flag every cell whose value is numeric AND whose number format is a date
 * format. Excel stores dates as numeric serial numbers and distinguishes
 * them purely by format string; there's no separate "date" value type.
 *
 * A format counts as a date format if, after stripping text literals
 * ("..."), escaped chars (\x), and color/condition bracket clauses ([..]),
 * the remaining tokens include any of:
 *   - 'y' (year)
 *   - 'd' (day)
 *   - 'mmm' or longer (month name; bare 'm' alone is ambiguous with minutes)
 */
const DATE_TOKEN_RE = /[yd]|m{3,}/i;

function isDateFormat(fmt: string | undefined | null): boolean {
  if (!fmt) return false;
  const stripped = fmt
    .replace(/"[^"]*"/g, "") // text literals
    .replace(/\\./g, "") // escaped characters
    .replace(/\[[^\]]*\]/g, ""); // color / condition / locale clauses
  return DATE_TOKEN_RE.test(stripped);
}

export const dateFormatCells: AuditRule = {
  id: "system.audit.date-format-cells",
  name: "Date cells",
  description: "Cells with a numeric value formatted as a date (year/day, or named month).",
  enabled: true,
  source: "system",
  severity: "info",
  /**
   * Two layered wins:
   *
   *   1. ValueKind.DATE as a pre-filter. The sink's date-format
   *      heuristic is BROADER than this rule's strict predicate (sink:
   *      any y/m/d/h/s or AM/PM; rule: only y, d, or mmm+). Every cell
   *      this rule would flag is sink-classified as DATE, so we can
   *      skip every non-DATE cell without inspecting its format
   *      string. NUMBER cells with bare-minute or time-only formats
   *      are correctly excluded.
   *
   *   2. Per-format-id cache. Number formats intern aggressively —
   *      typical workbooks have 10–100 distinct formats. The strict
   *      isDateFormat regex runs once per format id, not once per cell.
   *
   * Together: a 1M-cell sheet with 20 distinct formats and 5% DATE-
   * classified cells does ~20 regex evaluations and ~50k loop
   * iterations of the strict check, instead of 1M of each.
   */
  check(sheet, model) {
    const findings: AuditFindingPartial[] = [];
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const cellValueKind = sheet.cellValueKind;
    const numFmtIds = sheet.numFmtIds;
    const numFmtStrings = model.catalogs.numFmtStrings;

    // Per-format-id verdict cache. Format ids never collide with -1
    // (which means "no format"), so Map<number, boolean> is safe.
    const formatIsDate = new Map<number, boolean>();

    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      for (let c = 0; c < cols; c++) {
        const idx = rowBase + c;

        // Pre-filter via the sink's broader DATE class.
        if (cellValueKind[idx] !== ValueKind.DATE) continue;

        const fmtId = numFmtIds[idx];
        if (fmtId < 0) continue;

        let isDate = formatIsDate.get(fmtId);
        if (isDate === undefined) {
          isDate = isDateFormat(numFmtStrings[fmtId]);
          formatIsDate.set(fmtId, isDate);
        }
        if (!isDate) continue;

        findings.push({
          sheetName: sheet.name,
          address: addressFromRC(startRow + r, startCol + c),
          message: `date cell (format: ${numFmtStrings[fmtId]})`,
        });
      }
    }
    return findings;
  },
};
